/* eslint-disable no-param-reassign */
import { MOBILE_HEAD_HEIGHT } from '@src/js/constant';
import { isPortrait } from '@src/js/common/utils';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { InjectorService, MobileContextMenuService } from '@tencent/workbench';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { MobileEditorLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile';
import { reaction } from 'mobx';
import {
    IMobileRightMenuMananger,
    IMobileRightServiceDelegates,
    IPreviewShowSlideMenuConfig,
    IRect,
    IShowNoteAreaMenuConfig,
    IShowSlideMenuConfig,
} from '@src/slide/application/service/mobile-right-menu-manager/core/index.interface';

const NOTE_MIN_BOTTOM_PADDING = 65; // 顶部无空间且底部空间不足65时，备注的右键菜单居中显示

/**
 * 右键菜单管理
 */
export class MobileRightMenuMananger implements IMobileRightMenuMananger {
    public isClickOnElement = false;
    public showMenuTimeout: NodeJS.Timer|null;
    private services: IMobileRightServiceDelegates;

    private reaction = reaction(
        () => [
            this.getSelectionService()?.currentSlide,
            this.getSelectionService()?.lastMouseDownRegion,
        ],
        () => {
            this.showMenuTimeout && clearTimeout(this.showMenuTimeout);
        },
    );
    private timer: NodeJS.Timer|null = null;

    public setServiceDelegates(services: Partial<IMobileRightServiceDelegates>) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public removeEvents = () => {
        document.body.removeEventListener('touchstart', this.bodyClickHandler, true);
        browser.isIpadAndTencentDocApp && document.body.removeEventListener('mousedown', this.bodyClickHandler);
        this.timer && clearTimeout(this.timer);
    };

    public hideMenu() {
        const mobileContextMenuService = InjectorService.get(MobileContextMenuService);
        mobileContextMenuService.hideContextMenu();
        this.removeEvents();
    }

    public showMenu(wrapperRect?: IRect, delay = 150) {
        if (this.showMenuTimeout) {
            clearTimeout(this.showMenuTimeout);
        }

        if (this.services.toolbarStateManager.isMobileToolbarClick) {
            this.services.toolbarStateManager.isMobileToolbarClick = false;
            return;
        }

        this.showMenuTimeout = setTimeout(() => {
            this.showMenuLogicCanvas(wrapperRect);
        }, delay);
    }

    public isContextMenuShow() {
        const mobileContextMenuService = InjectorService.get(MobileContextMenuService);
        return mobileContextMenuService.isContextMenuShow();
    }

    public showNoteAreaMenu(config: IShowNoteAreaMenuConfig) {
        const newConfig: any = {
            top: config.top,
            left: (config.left + config.right) / 2,
            bottom: config.bottom,
        };
        const { container } = config;

        let containerRect;
        if (container !== null && typeof (container) !== 'undefined') {
            // 修正因为canvas滚动条引发的位置偏差
            containerRect = container.getBoundingClientRect();
            newConfig.top = newConfig.top - container.scrollTop + config.offsetTop;
            newConfig.bottom = newConfig.bottom - container.scrollTop + config.offsetTop;
            newConfig.left -= container.scrollLeft;
            newConfig.top += containerRect.top;
            newConfig.bottom += containerRect.top;
            newConfig.left += containerRect.left;
            if (newConfig.bottom > containerRect.height - NOTE_MIN_BOTTOM_PADDING) {
                newConfig.offsetAfterPostionChanged = containerRect.height / 2;
            }
        }

        const scroll = document.documentElement.scrollTop || document.body.scrollTop || 0;
        const option: any = containerRect ? {
            boundingRectLeft: 0,
            boundingRectRight: containerRect.right,
            // 客户端顶部栏会遮挡, 需要额外加上顶部栏高度
            boundingRectTop: MOBILE_HEAD_HEIGHT + (browser.isTencentDocApp ? MOBILE_HEAD_HEIGHT : 0),
            boundingRectBottom: containerRect.bottom + scroll,
        } : null;
        newConfig.top += scroll;
        option.offsetAfterPostionChanged = (newConfig.offsetAfterPostionChanged
            || newConfig.bottom - Math.max(newConfig.top, 0));

        const mobileContextMenuService = InjectorService.get(MobileContextMenuService);
        mobileContextMenuService.showContextMenu(newConfig.left, newConfig.top, false, option);
        this.addEvents();
    }

    public showSlideMenu(config: IShowSlideMenuConfig) {
        const targetRect = config.target?.getBoundingClientRect?.();
        if (!targetRect) {
            return;
        }
        const container = document.getElementById('slide-list-m-container');
        const containerRect = container?.getBoundingClientRect();
        const mobileContextMenuService = InjectorService.get(MobileContextMenuService);
        // 缩略图是否在左侧
        const isSlideListInLeft = (!isPortrait() || browser.isWideScreenDevice);
        mobileContextMenuService.showContextMenu(
            config.left, targetRect.top, false,
            // @ts-ignore ts化时发现缺了个boundingRectBottom，无法确定给这个默认值的影响，只能先ignore
            containerRect ? {
                boundingRectLeft: 0,
                boundingRectRight: isSlideListInLeft
                    ? document.documentElement.clientWidth : containerRect.right,
                // 缩略图在左侧时，限制右键菜单不能超出左侧缩略图顶部区域，否则会被顶部导航条挡住
                boundingRectTop: isSlideListInLeft ? containerRect.top : 0,
                // 超出区域限制后，上下文菜单会翻转，翻转后的offset
                offsetAfterPostionChanged: isSlideListInLeft ? Math.abs(targetRect.bottom - targetRect.top) : 0,
            } : undefined,
        );
        this.addEvents();
    }

    /**
     * 预览页单页菜单
     * @param config
     */
    public showSlideMenuInPreview(config: IPreviewShowSlideMenuConfig) {
        const option: any = config.boundingRect ? {
            boundingRectLeft: config.boundingRect.left,
            boundingRectRight: config.boundingRect.right,
            boundingRectTop: config.boundingRect.top,
            boundingRectBottom: config.boundingRect.bottom,
            offsetAfterPostionChanged:
                Math.abs(config.bottom) - (config.top > 0 ? config.top : config.boundingRect.top),
        } : null;

        const mobileContextMenuService = InjectorService.get(MobileContextMenuService);
        const left = (config.left + config.right) / 2;
        mobileContextMenuService.showContextMenu(
            left,
            config.top,
            false,
            option,
        );
    }

    private showMenuLogicCanvas(elementRect: IRect|undefined) {
        if (this.getUserPriviledgeService().pptEditorState > PPTEditorStateType.EDITABLE) {
            return;
        }

        const container = document.getElementById('canvas');
        if (!container) {
            return;
        }
        const containerRect = container.getBoundingClientRect();
        const htmlScrollTop = document.querySelector('html')?.scrollTop || 0;
        const option: any = containerRect ? {
            boundingRectLeft: 0,
            boundingRectRight: containerRect.right,
            boundingRectTop: containerRect.top - htmlScrollTop,
            boundingRectBottom: containerRect.bottom + htmlScrollTop,
        } : null;

        let domRect;
        if (!elementRect) {
            const model = this.getSelectionService().selectedElements?.selectedArray?.[0];
            if (model) {
                const element = document.querySelector(`#slide-element-${model.key}`);
                domRect = element?.getBoundingClientRect();
            }
        }

        const { top = 0, bottom = 0 } = elementRect || domRect || {};
        option.offsetAfterPostionChanged = Math.abs(bottom) - Math.abs(top);
        const newConfig = this.getTargetPosition(containerRect, elementRect, domRect);

        const mobileContextMenuService = InjectorService.get(MobileContextMenuService);
        mobileContextMenuService.showContextMenu(newConfig.left, newConfig.top, false, option);
        this.addEvents();
    }

    private getTargetPosition(containerRect: IRect, elementRect: IRect|undefined, domRect: IRect) {
        const newConfig = {
            left: containerRect.left,
            top: containerRect.top,
        };

        const model = this.getSelectionService().selectedElements?.selectedArray?.[0];

        if (model && elementRect) {
            // 选中 canvas 元素
            const { canvasSize } = this.services.contentRendererService;

            newConfig.left += ((elementRect.left + elementRect.right) / 2) + canvasSize.left;
            newConfig.top += elementRect.top + canvasSize.top;
        } else if (model && domRect) {
            // 选中 dom 元素
            newConfig.left = (domRect.left + domRect.right) / 2;
            newConfig.top = domRect.top;
        } else {
            // 如果点击空白位置，则在幻灯片中间的顶部显示粘贴菜单
            const { contentSize } = this.services.contentRendererService;

            newConfig.top += contentSize.top + contentSize.cTop - 5;
            newConfig.left += (containerRect.width || 0) / 2;
        }
        return newConfig;
    }

    private bodyClickHandler = (e) => {
        this.isClickOnElement = false;
        // 点击到元素上时，工具栏的隐藏由元素控制
        if (this.isClickOnElement) return;
        const clickOnMenu = this.clickOnMenu(e, 15);
        if (!clickOnMenu) {
            this.addLaterEvents();
        }
    };

    // 点击的位置是否在菜单组件上
    private clickOnMenu = (e, maxLevel) => {
        let curDom = e.target;
        let level = 0;
        while (curDom && level < maxLevel) {  // 只查到15层，避免过多查找
            curDom = curDom.parentNode;
            // eslint-disable-next-line no-plusplus
            level++;
            if (curDom?.classList?.value?.includes('mobile-context-menu_contextmenu-mobile-submenus-list')) {
                return true;
            }
        }
        return false;
    };

    private addEvents = () => {
        document.body.addEventListener('touchstart', this.bodyClickHandler, true);
        if (browser.isIpadAndTencentDocApp) {
            this.timer && clearTimeout(this.timer);
            this.timer = setTimeout(() => { // 添加延迟，防止长按时误触发mousedown事件
                document.body.addEventListener('mousedown', this.bodyClickHandler);
            }, 500);
        }
    };

    private addLaterEvents = () => {
        document.body.addEventListener('touchend', this.touchEndHandler, true);
    };


    private touchEndHandler = () => {
        setTimeout(() => {
            this.hideMenu();
        }, 50);
        document.body.removeEventListener('touchend', this.touchEndHandler, true);
    };

    private getSelectionService() {
        return this.services?.selectionService;
    }

    private getUserPriviledgeService() {
        return this.services?.userPriviledgeService;
    }
}
